import { useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCircleInfo,
  faPenToSquare,
  faRightFromBracket,
  faUserCheck,
  faUserGear,
  faUserSlash,
  faXmark,
} from '@fortawesome/free-solid-svg-icons';
import type { PlatformUserSummary } from '@/types/platform-user.types';
import { RoleBadge, StatusBadge, formatLastSignIn, formatUserDateTime } from './user-presentation';
import type { UserAction } from './PlatformUserActionDialog';

/**
 * Phase 12.6-4 — Platform Users detail drawer.
 *
 * Design of record: `PLATFORM_USERS_DESIGN.md` v1.0 §5.4.
 *
 * ── A drawer, not a route ───────────────────────────────────────────────────
 * §5.4 is explicit: right-side drawer held in **component state**, matching
 * Communication and Companies. That keeps the feature on a single route and
 * avoids promising a deep-link contract a later phase may need to change. §9
 * records that a deep link can be added later without restructuring — which is
 * true only while the drawer stays component state.
 *
 * ── Nothing sensitive is rendered ───────────────────────────────────────────
 * It shows the eight fields the API returns and no more. `passwordHash`,
 * `tokenVersion`, `passwordChangedAt` and `updatedAt` are not merely omitted
 * here — the API never sends them (12.6-2 §3.1), so there is nothing to omit.
 * The raw `id` is deliberately not displayed either: §5.2 records that a UUID
 * helps nobody.
 *
 * ── Actions (12.6-5) ────────────────────────────────────────────────────────
 * §6's per-user actions live here, not in a table column: with a handful of
 * accounts, a per-row overflow menu would put irreversible-feeling actions one
 * stray click from the cursor.
 *
 * Every one is gated on `platform:platform-users:manage`, which D-041 §5.2
 * grants to `platform-owner` ALONE. §5.5 requires them **hidden, not disabled**
 * for a read-only role — a greyed-out button invites a support ticket asking why
 * it is greyed out. **Hiding is presentation, never the security boundary:** the
 * backend refuses the same calls with 403, proven over real HTTP in 12.6-2.
 *
 * ── Self-mutation ───────────────────────────────────────────────────────────
 * Role change and deactivation against yourself are refused **by the server**,
 * so the controls are absent and the banner says why. Revoke-sessions against
 * yourself is deliberately PERMITTED (§6) — a compromise response, not an
 * escalation — and warns that it signs you out. Identity editing is permitted
 * too; an email change signs you out, which its own dialog warns about.
 *
 * `create` is not here: it belongs to the page header, not to a user's drawer.
 */
export function PlatformUserDetailDrawer({
  user,
  isSelf,
  canManage,
  onClose,
  onAction,
  onEditIdentity,
}: {
  user: PlatformUserSummary;
  /**
   * Matched by **id**, never by email or username — an address can be edited or
   * repeated in a way an identity cannot. Resolved by the caller from
   * `usePlatformAuthStore`.
   */
  isSelf: boolean;
  /** `canPlatform(role, 'platform:platform-users:manage')`, resolved by the page. */
  canManage: boolean;
  onClose: () => void;
  onAction: (action: UserAction) => void;
  onEditIdentity: () => void;
}) {
  const closeRef = useRef<HTMLButtonElement>(null);

  // Escape closes, matching every other overlay in the console. Focus moves to
  // the close button on open so a keyboard user is inside the dialog rather than
  // still on the row behind it.
  useEffect(() => {
    closeRef.current?.focus();
    const onKeyDown = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  return (
    <>
      {/* Overlay. Clicking it closes — the drawer is read-only, so there is no
          unsaved work a stray click could discard. */}
      <div
        aria-hidden="true"
        onClick={onClose}
        className="fixed inset-0 z-40 bg-black/40 backdrop-blur-[1px]"
      />

      {/* `w-full` below `sm` is the full-screen sheet §5.6 requires; from `sm`
          up it becomes a right-side panel. */}
      <aside
        role="dialog"
        aria-modal="true"
        aria-label={`${user.displayName} details`}
        className="fixed inset-y-0 right-0 z-50 flex w-full flex-col bg-card shadow-2xl sm:max-w-md"
      >
        <header className="flex items-start justify-between gap-3 border-b border-border px-5 py-4">
          <div className="min-w-0">
            <h2 className="truncate text-base font-semibold text-foreground">{user.displayName}</h2>
            <p className="mt-0.5 truncate text-sm text-muted-foreground">{user.email}</p>
          </div>
          <button
            ref={closeRef}
            type="button"
            onClick={onClose}
            aria-label="Close details"
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          >
            <FontAwesomeIcon icon={faXmark} className="h-4 w-4" />
          </button>
        </header>

        <div className="flex-1 space-y-5 overflow-y-auto px-5 py-5">
          {/* ── Self banner (§5.4) ──────────────────────────────────────────
              Present so the absent controls read as deliberate rather than
              broken. The restriction it names is enforced server-side: the
              admin service refuses a self role change and a self deactivation
              outright (verified in 12.6-2). */}
          {isSelf && (
            <div
              role="note"
              className="flex items-start gap-2.5 rounded-lg border border-border bg-muted/40 px-3.5 py-3"
            >
              <FontAwesomeIcon
                icon={faCircleInfo}
                className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground"
              />
              <p className="text-xs text-muted-foreground">
                This is your own account. Your <strong>role</strong> and <strong>status</strong>{' '}
                cannot be changed here — the server refuses it, so the controls are absent rather
                than hidden. You can still edit your identity and revoke your own sessions.
              </p>
            </div>
          )}

          <Section title="Identity">
            <Field label="Display name" value={user.displayName} />
            <Field label="Email" value={user.email} />
            {/* An absent username is a real state, not an error. */}
            <Field label="Username" value={user.username ?? '—'} />
          </Section>

          <Section title="Access">
            <Row label="Role">
              <RoleBadge role={user.role} />
            </Row>
            <Row label="Status">
              <StatusBadge isActive={user.isActive} />
            </Row>
          </Section>

          <Section title="Activity">
            {/* "Never" rather than an em dash — §5.2. An account that has never
                been used reads differently from one with no date recorded. */}
            <Field
              label="Last sign-in"
              value={formatLastSignIn(user.lastLoginAt)}
              title={user.lastLoginAt ? formatUserDateTime(user.lastLoginAt) : undefined}
            />
            <Field label="Created" value={formatUserDateTime(user.createdAt)} />
          </Section>

          {/* ── Actions (§6) ─────────────────────────────────────────────────
              Read-only identity above; everything that mutates is below the
              divider, so the two never blur together. */}
          {canManage ? (
            <section className="border-t border-border pt-4">
              <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Actions
              </h3>
              <div className="grid grid-cols-1 gap-2">
                <ActionButton
                  icon={faPenToSquare}
                  label="Edit identity"
                  hint="Display name, email or username"
                  onClick={onEditIdentity}
                />

                {/* Refused server-side against yourself, so not offered. */}
                {!isSelf && (
                  <ActionButton
                    icon={faUserGear}
                    label="Change role"
                    hint="Ends their sessions immediately"
                    onClick={() => onAction('role')}
                  />
                )}

                {!isSelf &&
                  (user.isActive ? (
                    <ActionButton
                      icon={faUserSlash}
                      label="Deactivate"
                      hint="Blocks sign-in and ends their sessions"
                      destructive
                      onClick={() => onAction('deactivate')}
                    />
                  ) : (
                    <ActionButton
                      icon={faUserCheck}
                      label="Activate"
                      hint="Restores sign-in"
                      onClick={() => onAction('activate')}
                    />
                  ))}

                {/* Permitted against yourself — §6. */}
                <ActionButton
                  icon={faRightFromBracket}
                  label="Revoke sessions"
                  hint={isSelf ? 'Signs YOU out immediately' : 'Signs this account out everywhere'}
                  destructive
                  onClick={() => onAction('revoke-sessions')}
                />
              </div>
            </section>
          ) : (
            /* ── The platform-admin limitation, stated plainly ──────────────
               Rather than an unexplained absence of controls. It says what is
               required, without implying this role could obtain it here, and
               without offering a workaround. */
            <section className="border-t border-border pt-4">
              <p className="text-xs text-muted-foreground">
                Managing platform users — creating, editing, changing roles, deactivating and
                revoking sessions — requires the <strong>Platform Owner</strong> role. Your role can
                view this page.
              </p>
            </section>
          )}
        </div>
      </aside>
    </>
  );
}

/**
 * One action row. Full-width and `min-h-11` (44px) so it is a comfortable touch
 * target in the full-screen sheet, with the consequence stated under the label
 * rather than discovered in the confirmation dialog.
 */
function ActionButton({
  icon,
  label,
  hint,
  destructive = false,
  onClick,
}: {
  icon: typeof faPenToSquare;
  label: string;
  hint: string;
  destructive?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex min-h-11 w-full items-start gap-3 rounded-lg border px-3.5 py-2.5 text-left transition-colors focus:outline-none focus:ring-2 focus:ring-ring ${
        destructive
          ? 'border-destructive/30 hover:bg-destructive/5'
          : 'border-border hover:bg-muted'
      }`}
    >
      <FontAwesomeIcon
        icon={icon}
        className={`mt-0.5 h-3.5 w-3.5 shrink-0 ${destructive ? 'text-destructive' : 'text-muted-foreground'}`}
      />
      <span className="min-w-0">
        <span
          className={`block text-sm font-medium ${destructive ? 'text-destructive' : 'text-foreground'}`}
        >
          {label}
        </span>
        <span className="block text-xs text-muted-foreground">{hint}</span>
      </span>
    </button>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {title}
      </h3>
      <dl className="space-y-2.5 rounded-lg border border-border bg-background/40 px-3.5 py-3">
        {children}
      </dl>
    </section>
  );
}

function Field({ label, value, title }: { label: string; value: string; title?: string }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <dt className="shrink-0 text-xs text-muted-foreground">{label}</dt>
      <dd className="min-w-0 break-words text-right text-sm text-foreground" title={title}>
        {value}
      </dd>
    </div>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <dt className="shrink-0 text-xs text-muted-foreground">{label}</dt>
      <dd className="min-w-0">{children}</dd>
    </div>
  );
}
